import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MobileFrame from '../components/MobileFrame';
import axios from 'axios';
import { proceedToLenderJourney } from '../services/preApprovalService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://staging.api.pay.cleverpe.com/api';

interface SchemeResponse {
  amount: string;
  scheme: {
    _id: string;
    name: string;
    grossTenure: number;
    advancedEMI: number;
    annualInterest: number;
  };
  calculations: {
    monthlyEMI: number;
    totalInterestPayable: number;
    advancedEmiPayment: number;
    grossTenure: number;
    customerROI: number;
    totalAmount: number;
    netLoanAmount: number;
    partnerTotalDownPayment: number;
    lenderDownPayment: number;
  };
  display: string;
}

interface LocationState {
  preApprovalData: any;
  transactionData: any;
  orderId: string;
  lender: string;
  schemeSelected: string;
  partner: string;
}

const EmiTenure: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [schemes, setSchemes] = useState<SchemeResponse[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  // Fetch schemes on mount
  useEffect(() => {
    const mobileNumber = sessionStorage.getItem('mobileNumber');
    if (!mobileNumber) {
      navigate('/login');
      return;
    }

    if (!state || !state.transactionData) {
      navigate('/response');
      return;
    }
    const partnerName = sessionStorage.getItem('partnerName');

    const fetchSchemes = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/scheme/get`, {
          orderId: state.orderId,
          lender: state.lender,
          amount: state.transactionData.amount,
          gatewayPartner: partnerName,
        });

        if (response.data?.success) {
          setSchemes(response.data.schemes);
        } else {
          setError('Failed to load EMI options');
        }
      } catch (err: any) {
        // console.error('Error fetching EMI options:', err);
        setError('Unable to fetch EMI options. Please try again later.');
      }
    };

    fetchSchemes();
  }, [navigate, state]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOptionSelect = (index: number) => {
    setSelectedOption(index);
  };

  const clearSessionStorage = () => {
    sessionStorage.removeItem('preApprovalResult');
    sessionStorage.removeItem('transactionData');
    sessionStorage.removeItem('approvedAmount');
    sessionStorage.removeItem('lenderName');
    sessionStorage.removeItem('selectedEmiOption');
  };

  const handleContinue = async () => {
    if (selectedOption === null) {
      setError('Please select an EMI tenure option');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const selectedSchemeOption = schemes[selectedOption];
      const schemeId = selectedSchemeOption.scheme._id; // This is what gets passed

      // Save selected EMI option if needed
      sessionStorage.setItem('selectedEmiOption', JSON.stringify(selectedSchemeOption));

      if (!state || !state.orderId) {
        throw new Error('Missing required data to proceed to lender');
      }

      // ✅ Call your helper function here instead of fetch directly
      const result = await proceedToLenderJourney(
        state.orderId,
        schemeId // 👈 Make sure _id is ObjectId or string based on backend
      );

      clearSessionStorage();

      if (result?.redirectionLink) {
        window.location.href = result?.redirectionLink;
      } else {
        throw new Error('No redirection link received from server');
      }

    } catch (err: any) {
      // console.error('Error processing EMI selection:', err);
      setError(err.message || 'Failed to process your selection. Please try again.');
      setIsLoading(false);
    }
  };
  const breakdown =
    selectedOption !== null ? schemes[selectedOption]?.calculations : schemes?.[0]?.calculations;


  const EmiTenureContent = () => (
    <div className="flex flex-col h-full">
      {/* Page indicator */}
      <div className="flex items-center justify-end mb-2">
        <div className="text-base font-semibold text-gray-700 dark:text-gray-300">
          3<span className="font-normal text-gray-400 dark:text-gray-500">/3</span>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto pr-1 scrollbar-hide pb-15">
          {/* Header */}
          {state.partner === "VIFIN" && (
            <>
              <div className="mb-2">
                <h1 className="text-md font-semibold text-gray-900 dark:text-white">
                  Payment Breakdown
                </h1>
              </div>

              {/* Breakdown Card */}

              <div className="mb-4">
                <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm px-5 py-3 flex justify-between items-center space-x-4">

                  {/* Total Amount */}
                  <div className="flex flex-col leading-tight">
                    <span className="text-gray-500 dark:text-gray-400 text-[11px]">
                      Total Amount
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white text-[13px]">
                      ₹{breakdown?.totalAmount ?? "--"}
                    </span>
                  </div>

                  {/* Net Loan Amount */}
                  <div className="flex flex-col leading-tight">
                    <span className="text-gray-500 dark:text-gray-400 text-[11px]">
                      Net Loan Amount
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white text-[13px]">
                      ₹{breakdown?.netLoanAmount ?? "--"}
                    </span>
                  </div>

                  {/* Down Payment */}
                  <div className="flex flex-col leading-tight">
                    <span className="text-gray-500 dark:text-gray-400 text-[11px]">
                      Down Payment
                    </span>
                    <span className="font-semibold text-green-600 dark:text-green-400 text-[13px]">
                      ₹{breakdown?.partnerTotalDownPayment ?? breakdown?.lenderDownPayment ?? "--"}
                    </span>
                  </div>

                </div>

                {/* Note */}
                <div className="mt-1 px-2 text-[10px] text-gray-500 dark:text-gray-400 leading-snug">
                  *Down payment will be collected after successful transaction completion.
                </div>
              </div>
            </>
          )}

          {/* EMI Tenure Heading */}
          <div className="mb-2 mt-9 md:mt-2">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              Select EMI Tenure
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Choose your preferred EMI tenure
            </p>
          </div>

          {/* EMI Options */}
          <div className="space-y-2">
            {schemes.map((option, index) => {
              const { calculations, scheme } = option;
              const netEMIMonths = scheme.grossTenure - scheme.advancedEMI;

              return (
                <button
                  key={index}
                  onClick={() => handleOptionSelect(index)}
                  className={`relative w-full py-1.5 px-5 rounded-full flex justify-between items-center border min-h-[44px] transition-all duration-150 ${selectedOption === index
                      ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                      : "border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-card"
                    }`}
                >
                  {/* Left section */}
                  <div className="flex flex-col text-left pl-1">
                    <span className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                      ₹{calculations.monthlyEMI} x {netEMIMonths} months
                    </span>

                    {scheme.advancedEMI > 0 && (
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight mt-[1px]">
                        + ₹{calculations.advancedEmiPayment} (Advanced EMI {scheme.advancedEMI}m)
                      </span>
                    )}
                  </div>

                  {/* Right section */}
                  <div className="text-xs text-gray-500 dark:text-gray-400 text-right pr-1">
                    {calculations.customerROI}% annually
                  </div>

                  {/* Recommended tag */}
                  {index === 0 && (
                    <div className="absolute -top-2 right-4 px-2 py-[0.75px] text-[9.5px] font-medium text-white bg-green-500 rounded-full shadow-sm">
                      Recommended
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Disclaimer */}
          <div className="pl-6 text-[9px] text-gray-500 dark:text-gray-400 mt-2 mb-6 space-y-1">
            <div>*EMI varies based on eligibility.</div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 px-4 py-2 text-sm text-center text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded max-w-full overflow-hidden text-ellipsis whitespace-pre-line break-words">
          {error}
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto">
        {/* Powered by */}
        <div className="flex items-center justify-center mb-4 mt-2">
          <img
            src="/CleverPeLogos/PoweredBy.svg"
            alt="Powered By CleverPe"
            className="h-10"
            onError={(e) => {
              e.currentTarget.src =
                "https://placehold.co/140x40?text=Powered+By+CleverPe";
            }}
          />
        </div>

        {/* Continue button */}
        <button
          onClick={handleContinue}
          disabled={isLoading || selectedOption === null}
          className={`w-full py-3 text-lg font-semibold text-center text-white transition rounded-full ${isLoading || selectedOption === null
            ? "bg-gray-400 dark:bg-gray-700 dark:text-gray-500"
            : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md active:scale-[0.97]"
            }`}
        >
          {isLoading ? "Processing..." : "Continue"}
        </button>
      </div>
    </div>
  );

  return isMobile ? (
    <div className="h-[100dvh] w-full p-5 overflow-hidden bg-white dark:bg-dark">
      <EmiTenureContent />
    </div>
  ) : (
    <MobileFrame>
      <EmiTenureContent />
    </MobileFrame>
  );
};

export default EmiTenure;
